'use client'

import {observer, useLocalObservable} from 'mobx-react'
import React, {useContext, useEffect} from 'react'

import {getInjection} from '@runlab/di'

import {useLayoutStore} from '@/src/shared/ui/layout/ui/provider'
import {BasketStoreInterface} from '@/src/presentation/stores/basket/basket.store.interface'
import {BasketEventTypes, EventSubscription} from '@/src/core/domain/events'
import {AsyncProcessLoading} from '@/src/shared/ui/layout/ui/async-loading'

const BasketContext = React.createContext<Nullable<BasketStoreInterface>>(null)

export const BasketProvider = observer(({children}: React.PropsWithChildren) => {
  const layoutStore = useLayoutStore()
  const basketStore = useLocalObservable(() => {
    const store = getInjection('BasketStoreInterface')

    if (layoutStore.data.header.cart) {
      store.setBasketData(layoutStore.data.header.cart)
      store.setLoading(false)
    } else if (!layoutStore.data.session?.hasPhpSession && !layoutStore.data.session?.user?.id) {
      store.setLoading(false)
    }

    return store
  })

  useEffect(() => {
    if (layoutStore.data.header.cart) {
      basketStore.setBasketData(layoutStore.data.header.cart)
      basketStore.setLoading(false)
    } else if (!layoutStore.data.session) {
      basketStore.setBasketData(null)
      basketStore.setLoading(false)
    }
  }, [layoutStore.data.header.cart, layoutStore.data.session])

  useEffect(() => {
    const subs: EventSubscription[] = []

    subs.push(
      getInjection('IEventBus').subscribe(BasketEventTypes.BasketProcessStart, () => {
        basketStore.setPending(true)
      }),
    )

    subs.push(
      getInjection('IEventBus').subscribe(BasketEventTypes.BasketProcessFinish, () => {
        basketStore.setPending(false)
      }),
    )

    return () => {
      for (let i = 0; i < subs.length; i++) {
        subs[i]!.unsubscribe()
      }
    }
  }, [])

  return (
    <BasketContext.Provider value={basketStore}>
      {children}
      {basketStore.pending && <AsyncProcessLoading />}
    </BasketContext.Provider>
  )
})

export const useBasket = () => {
  const basketStore = useContext(BasketContext)

  if (!basketStore) throw new Error('useBasket() must be used within BasketProvider')

  return basketStore
}
